import qapi from "views_shared/app/javascript/api";
import { throwOnApiError } from "views_shared/app/javascript/api/api";

// Context-fed action hook for represented experts — mirrors use_pr_profile_update_api. The provider owns
// the `representedExperts` collection; this only performs the writes against it (optimistic update on the
// provider state + snapshot rollback + throw-on-error), routing failures to the shared generalError and
// reconciling via silentReload.
//
// representations.create/destroy are shared with the Products editor (which reads the { data }/{ error }
// shape directly), so their client contract is left intact and this caller opts into throw-on-error at the
// call site via throwOnApiError.
const usePrExpertsApi = ({
  prProfileId,
  representedExperts,
  setRepresentedExperts,
  silentReload,
  setGeneralError,
  setIsUpdating,
  isUpdating,
}) => {
  // Re-sync page state from whatever the server actually committed after a failed write — the local
  // rollback restores the pre-write collection, which is only right if the write truly did nothing (a
  // partially-applied fan-out destroy is exactly the case where it isn't). Nonfatal so a refresh that
  // also fails cannot escalate a recoverable, already-surfaced error into the page error boundary.
  const reconcile = async () => {
    try {
      await silentReload({ nonfatal: true });
    } catch {
      // Deliberately empty: the failure is already on generalError.
    }
  };

  // Resolves { saved: boolean } — additive: existing callers ignore the return, but it lets a caller
  // gate a post-add side effect (like requesting a tab switch) on a CONFIRMED save, matching the same
  // contract usePrRepresentationsApi's addProduct/addCompany return.
  const addExpert = async (expertId) => {
    if (isUpdating) return { saved: false }; // guard overlapping mutations — a whole-array snapshot can't survive concurrency
    setGeneralError(null);
    setIsUpdating(true);
    try {
      throwOnApiError(await qapi.representations.create({ promoterProfileId: prProfileId, promoEntityId: expertId }));
      await silentReload();
      return { saved: true };
    } catch (e) {
      setGeneralError(e);
      return { saved: false };
    } finally {
      setIsUpdating(false);
    }
  };

  // Resolves whether the expert was removed, which is what closes the section's confirmation.
  const removeExpert = async (expertId) => {
    if (isUpdating) return false;
    setGeneralError(null);
    const snapshot = representedExperts;

    // Optimistically drop the expert from the provider collection (functional update — no stale closure).
    setRepresentedExperts((current) => current.filter((e) => e.id !== expertId));

    const representationIds = snapshot
      .filter((e) => e.id === expertId)
      .flatMap((e) => e.representatives || [])
      .filter((r) => r.profile_id === prProfileId)
      .map((r) => r.representation_id);

    setIsUpdating(true);
    try {
      // allSettled, not all: an early rejection must not start the reconcile (or release the isUpdating
      // guard) while sibling destroys are still in flight — every one has to land before the collection
      // is re-read. The first failure still surfaces.
      const outcomes = await Promise.allSettled(
        representationIds.map(async (id) => throwOnApiError(await qapi.representations.destroy(id))),
      );
      const failed = outcomes.find((outcome) => outcome.status === "rejected");
      if (failed) throw failed.reason;
      await silentReload();
      return true;
    } catch (e) {
      setRepresentedExperts(snapshot); // roll back the optimistic removal
      setGeneralError(e);
      await reconcile();
      return false;
    } finally {
      setIsUpdating(false);
    }
  };

  return { addExpert, removeExpert };
};

export default usePrExpertsApi;
